import { useEffect, useState } from "react";
import { Inbox, Loader2, ChevronDown, ChevronUp, Globe } from "lucide-react";
import { api } from "@/lib/api";
import { fmtDate, fmtDT, fmtEUR } from "@/constants";
import { toast } from "sonner";

const KIND_LABEL = { photobooth: "Photobooth", agb: "Audio Guest Book", cabine: "Cabine AGB" };

const RESULTS = {
  all: { label: "Disponible", cls: "bg-emerald-100 text-emerald-700" },
  partial: { label: "Partiellement dispo", cls: "bg-amber-100 text-amber-800" },
  none: { label: "Indisponible", cls: "bg-rose-100 text-rose-700" },
  weekday: { label: "À confirmer — semaine", cls: "bg-amber-100 text-amber-800" },
  provisional: { label: "À confirmer — période provisoire", cls: "bg-amber-100 text-amber-800" },
  last_minute: { label: "Dernière minute — à confirmer", cls: "bg-orange-100 text-orange-800" },
  zone_confirm: { label: "Zone limite — à confirmer", cls: "bg-amber-100 text-amber-800" },
};

const STATUSES = ["Nouvelle", "Consultée", "A réservé / acompte vérifié", "N'a pas donné suite", "Refusée / indisponible"];

const STATUS_CLS = {
  "Nouvelle": "bg-sky-100 text-sky-800 border-sky-300",
  "Consultée": "bg-slate-100 text-slate-700 border-slate-300",
  "A réservé / acompte vérifié": "bg-emerald-100 text-emerald-800 border-emerald-300",
  "N'a pas donné suite": "bg-slate-100 text-slate-500 border-slate-200",
  "Refusée / indisponible": "bg-rose-100 text-rose-700 border-rose-300",
};

export default function DemandesWeb() {
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(null);
  const [tab, setTab] = useState("consultations");
  const [chats, setChats] = useState(null);
  const [openChat, setOpenChat] = useState(null);

  const load = () => api.get("/consultations").then((r) => setItems(r.data)).catch(() => toast.error("Chargement impossible"));
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (tab === "chatbot" && chats === null) {
      api.get("/chatbot/conversations").then((r) => setChats(r.data)).catch(() => toast.error("Chargement impossible"));
    }
  }, [tab, chats]);

  const setStatus = async (id, status) => {
    try {
      await api.put(`/consultations/${id}/status`, { status });
      setItems((arr) => arr.map((x) => (x.id === id ? { ...x, status } : x)));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  if (!items) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;

  return (
    <div className="space-y-5" data-testid="demandes-page">
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-xl bg-sky-500 flex items-center justify-center shrink-0">
          <Globe className="h-5 w-5 text-white" />
        </div>
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">Demandes site internet</h1>
          <p className="text-xs text-slate-500">
            Consultations du module public — {items.length} au total. Ces demandes ne bloquent jamais le matériel : vous validez vous-même la réservation après vérification de l'acompte.
          </p>
        </div>
      </div>

      <div className="flex gap-1.5" data-testid="demandes-tabs">
        {[["consultations", "Consultations"], ["chatbot", "Chatbot"]].map(([v, label]) => (
          <button
            key={v}
            data-testid={`demandes-tab-${v}`}
            onClick={() => setTab(v)}
            className={`rounded-full border px-4 py-2 text-sm font-semibold transition-colors ${tab === v ? "bg-slate-900 text-white border-slate-900" : "border-slate-200 bg-white text-slate-600 hover:border-sky-400"}`}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === "consultations" && items.length === 0 && (
        <p className="rounded-2xl border border-slate-200 bg-white p-6 text-sm text-slate-500" data-testid="demandes-empty">
          Aucune consultation pour le moment. Dès qu'un visiteur vérifie une disponibilité sur votre site, elle apparaît ici.
        </p>
      )}

      {tab === "consultations" && (
      <ul className="space-y-2">
        {items.map((c, i) => {
          const r = RESULTS[c.result] || RESULTS.none;
          const isOpen = open === c.id;
          return (
            <li key={c.id} data-testid={`demande-item-${i}`} className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
              <button
                type="button"
                data-testid={`demande-toggle-${i}`}
                onClick={() => setOpen(isOpen ? null : c.id)}
                className="w-full text-left p-3.5 sm:p-4 flex items-center gap-3 hover:bg-slate-50 transition-colors"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                    <span className="font-semibold text-sm">{fmtDate(c.event_date)}</span>
                    <span className={`text-[11px] font-bold rounded-full px-2 py-0.5 ${r.cls}`} data-testid={`demande-result-${i}`}>{r.label}</span>
                    <span className={`text-[11px] font-bold rounded-full border px-2 py-0.5 ${STATUS_CLS[c.status] || STATUS_CLS["Nouvelle"]}`} data-testid={`demande-status-badge-${i}`}>{c.status}</span>
                  </div>
                  <p className="text-xs text-slate-500 mt-1 truncate">
                    {(c.kinds || []).map((k) => KIND_LABEL[k] || k).join(" + ")}
                    {c.formula ? ` · formule ${c.formula}` : ""}
                    {c.city ? ` · ${c.city}` : ""}
                    {c.total ? (c.result === "partial" ? ` · ~${fmtEUR(c.total)} (matériel dispo. uniquement)` : ` · ${fmtEUR(c.total)}`) : ""}
                    {c.contact?.name ? ` · ${c.contact.name}` : ""}
                  </p>
                </div>
                <span className="text-xs text-slate-400 shrink-0 hidden sm:block">{fmtDT(c.at)}</span>
                {isOpen ? <ChevronUp className="h-4 w-4 text-slate-400 shrink-0" /> : <ChevronDown className="h-4 w-4 text-slate-400 shrink-0" />}
              </button>

              {isOpen && (
                <div className="border-t border-slate-100 p-4 space-y-3 text-sm" data-testid={`demande-detail-${i}`}>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-1.5">
                    <p><span className="text-slate-500">Consultée le :</span> {fmtDT(c.at)}</p>
                    <p><span className="text-slate-500">Événement :</span> {fmtDate(c.event_date)}</p>
                    <p><span className="text-slate-500">Type :</span> {c.event_type || "—"}</p>
                    <p><span className="text-slate-500">Matériel :</span> {(c.kinds || []).map((k) => KIND_LABEL[k] || k).join(" + ")}</p>
                    {c.formula && <p><span className="text-slate-500">Formule :</span> {c.formula} photos{c.formula_price ? ` — ${fmtEUR(c.formula_price)}` : ""}</p>}
                    {c.city && <p><span className="text-slate-500">Lieu :</span> {c.city}{c.postcode ? ` (${c.postcode})` : ""}</p>}
                    {c.supplement !== null && c.supplement !== undefined && c.supplement > 0 && <p><span className="text-slate-500">Supplément déplacement :</span> {fmtEUR(c.supplement)}</p>}
                    {c.total ? <p className="font-semibold"><span className="text-slate-500 font-normal">Total présenté :</span> {c.result === "partial" ? `~${fmtEUR(c.total)} (matériel disponible uniquement)` : fmtEUR(c.total)}</p> : null}
                    <p><span className="text-slate-500">Origine :</span> {c.origin}</p>
                  </div>
                  {c.availability && (
                    <p className="text-xs text-slate-500">
                      Détail : {(c.kinds || []).map((k) => `${KIND_LABEL[k] || k} ${c.availability[k] ? "disponible" : "indisponible"}`).join(" — ")}
                    </p>
                  )}
                  {c.contact ? (
                    <p className="rounded-lg bg-emerald-50 border border-emerald-200 px-3 py-2 text-sm" data-testid={`demande-contact-${i}`}>
                      <strong>{c.contact.name || "Sans nom"}</strong>
                      {c.contact.phone ? ` · ${c.contact.phone}` : ""}
                      {c.contact.email ? ` · ${c.contact.email}` : ""}
                    </p>
                  ) : (
                    <p className="text-xs text-slate-400">Aucune coordonnée laissée par le visiteur.</p>
                  )}
                  <div className="flex flex-wrap items-center gap-2 pt-1">
                    <span className="text-xs font-bold uppercase tracking-wider text-slate-400">Suivi commercial :</span>
                    {STATUSES.map((s) => (
                      <button
                        key={s}
                        type="button"
                        data-testid={`demande-status-${i}-${s}`}
                        onClick={() => setStatus(c.id, s)}
                        className={`rounded-full border px-3 py-1 text-xs font-semibold transition-colors ${c.status === s ? STATUS_CLS[s] : "border-slate-200 text-slate-500 hover:border-sky-400"}`}
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                  {(c.status === "A réservé / acompte vérifié") && (
                    <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                      Pensez à créer la réservation dans l'application pour bloquer réellement le matériel à cette date.
                    </p>
                  )}
                </div>
              )}
            </li>
          );
        })}
      </ul>
      )}

      {tab === "chatbot" && (
        !chats ? (
          <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
        ) : chats.length === 0 ? (
          <p className="rounded-2xl border border-slate-200 bg-white p-6 text-sm text-slate-500" data-testid="chatbot-empty">
            Aucune conversation chatbot pour le moment. Dès qu'un visiteur échange avec l'assistant du site, la conversation apparaît ici.
          </p>
        ) : (
          <ul className="space-y-2" data-testid="chatbot-list">
            {chats.map((c, i) => {
              const firstQ = (c.messages || []).find((m) => m.role === "user");
              const isOpen = openChat === c.id;
              return (
                <li key={c.id} data-testid={`chatbot-item-${i}`} className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
                  <button
                    type="button"
                    data-testid={`chatbot-toggle-${i}`}
                    onClick={() => setOpenChat(isOpen ? null : c.id)}
                    className="w-full text-left p-3.5 sm:p-4 flex items-center gap-3 hover:bg-slate-50 transition-colors"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold truncate">{firstQ ? firstQ.text : "Conversation"}</p>
                      <p className="text-xs text-slate-500 mt-1">
                        {fmtDT(c.created_at)} · {(c.messages || []).length} message{(c.messages || []).length > 1 ? "s" : ""}
                      </p>
                    </div>
                    {isOpen ? <ChevronUp className="h-4 w-4 text-slate-400 shrink-0" /> : <ChevronDown className="h-4 w-4 text-slate-400 shrink-0" />}
                  </button>
                  {isOpen && (
                    <div className="border-t border-slate-100 p-4 space-y-2" data-testid={`chatbot-detail-${i}`}>
                      {(c.messages || []).map((m, j) => (
                        <p key={j} className={`text-sm rounded-xl px-3 py-2 ${m.role === "user" ? "bg-sky-50 text-sky-900" : "bg-slate-50 text-slate-700"}`}>
                          <span className="block text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-0.5">{m.role === "user" ? "Visiteur" : "Assistant"}</span>
                          {m.text.replace(/\*\*/g, "")}
                        </p>
                      ))}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )
      )}
    </div>
  );
}
